import './styles.scss'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import clsx from 'clsx'
import Skeleton from 'react-loading-skeleton'

import FormatPrice from '@/components/FormatPrice'
import ImageWithFallBack from '@/components/ImageWithFallback'
import CustomButton from 'components/CustomButton'
import { useAppSelector } from '@/store/hook'
import { selectHideListPriceGlobal } from '@/store/selectors/configSelector'
import { linkGenerator } from '@/libs/utils/language'

import Tag from '../Tag'

interface SpecialItemProps {
  isLoading?: boolean
  tagName?: string
  img?: {
    src?: string
    alt?: string
  }
  name?: string
  materialNumber?: string
  listPrice?: number
  currencyUnit?: string
}

function SpecialItem(props: SpecialItemProps) {
  const {
    isLoading = false,
    tagName = '',
    img,
    name = '',
    materialNumber = '',
    listPrice = 0,
    currencyUnit = '',
  } = props

  const { t } = useTranslation()
  const navigate = useNavigate()

  const shouldHideListPriceGlobal = useAppSelector(selectHideListPriceGlobal)

  const renderImage = () =>
    isLoading ? <Skeleton className="special-item__image" /> : (
      <ImageWithFallBack
        alt={`${img?.alt} Product`}
        className="special-item__image"
        height={148}
        src={img?.src}
        width={148}
      />
    )

  const renderName = () => (
    <div
      className={clsx('special-item__name', {
        'special-item__name--loading': isLoading,
      })}
    >
      {isLoading ? <Skeleton height={20} /> : name}
    </div>
  )

  const renderMaterialNumber = () => (
    <div
      className={clsx('special-item__material-number', {
        'special-item__material-number--loading': isLoading,
      })}
    >
      {isLoading ? <Skeleton height={30} /> : (
        `${
          t('myWorkspace.specialsForYou.specialItem.itemNumber', {
            materialNumber,
          })
        }`
      )}
    </div>
  )

  const renderListPrice = () => (
    <div
      className={clsx('special-item__price--list', {
        'special-item__price--loading': isLoading,
      })}
    >
      {isLoading
        ? <Skeleton height={20} />
        : listPrice
        ? <FormatPrice currencyUnit={currencyUnit} price={listPrice} />
        : (
          t('myWorkspace.specialsForYou.inValidListPrice')
        )}
    </div>
  )

  const renderButton = () => (
    <CustomButton
      className="special-item__button"
      disabled={isLoading}
      onClick={() => {
        navigate(linkGenerator(`/product/${materialNumber}`))
      }}
    >
      {isLoading ? <Skeleton width={87} /> : (
        t('myWorkspace.specialsForYou.specialItem.btnTitle')
      )}
    </CustomButton>
  )

  return (
    <div className="special-item__wrapper">
      {tagName && <Tag isLoading={isLoading} name={tagName} />}
      <div className="special-item__middle-section__wrapper">
        <div className="special-item__middle-section--left">
          {renderImage()}
        </div>
        <div className="special-item__middle-section--right">
          {renderName()}
          {renderMaterialNumber()}
          {shouldHideListPriceGlobal ? null : (
            <div className="special-item__price__wrapper">
              {renderListPrice()}
            </div>
          )}
        </div>
      </div>
      <div className="special-item__lower-section__wrapper">
        <div className="special-item__lower-section--left" />
        <div className="special-item__lower-section--right">
          {renderButton()}
        </div>
      </div>
    </div>
  )
}

export default SpecialItem
